// macadamia-app/src/macadamia-core/components/settings/AuthorsSettings.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { IDossier, IAuthor, IInvitation } from './../widgets/types';
import { apiClient } from '../../../utils/apiClient';
import Trash from '../../icons/trash';

interface AuthorsSettingsProps {
  dossier: IDossier;
  onAddAuthor: (email: string) => void;
  onRemoveAuthor: (authorId: string) => void;
}

interface ErrorState {
  general?: string;
  invitation?: string;
  removal?: string;
}

const AuthorsSettings: React.FC<AuthorsSettingsProps> = ({ dossier, onAddAuthor, onRemoveAuthor }) => {
  const [email, setEmail] = useState('');
  const [coAuthors, setCoAuthors] = useState<IAuthor[]>(dossier.coAuthors || []);
  const [invitations, setInvitations] = useState<IInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<ErrorState>({});
  const [successMessage, setSuccessMessage] = useState('');

  const clearMessages = useCallback(() => {
    setErrors({});
    setSuccessMessage('');
  }, []);

  const handleApiError = useCallback((error: any, context: string): string => {
    console.error(`API Error in ${context}:`, error);

    if (error.status === 429) {
      return 'Trop de tentatives. Veuillez patienter avant de réessayer.';
    }

    if (error.status === 409) {
      return 'Cette personne a déjà été invitée sur ce dossier.';
    }

    if (error.status === 400 && error.data?.errors) {
      return error.data.errors.join(', ');
    }

    if (error.data?.message) {
      return error.data.message;
    }

    return 'Une erreur est survenue. Veuillez réessayer.';
  }, []);

  const fetchWithErrorHandling = useCallback(async (path: string, options: RequestInit = {}) => {
    const response = await apiClient(path, options);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Network error' }));
      const err = new Error(errorData.message || `HTTP ${response.status}`) as Error & {
        status: number;
        data: typeof errorData;
      };
      err.status = response.status;
      err.data = errorData;
      throw err;
    }

    return response.json();
  }, []);

  const fetchCoAuthors = useCallback(async () => {
    try {
      const data = await fetchWithErrorHandling(`/dossiers/${dossier._id}/authors`);
      setCoAuthors(data.coAuthors || []);
    } catch (error) {
      const errorMessage = handleApiError(error, 'fetchCoAuthors');
      setErrors(prev => ({ ...prev, general: errorMessage }));
    }
  }, [dossier._id, fetchWithErrorHandling, handleApiError]);

  const fetchInvitations = useCallback(async () => {
    try {
      const invitationsData = await fetchWithErrorHandling(`/invitations/dossier/${dossier._id}`);
      setInvitations(invitationsData || []);
    } catch (error) {
      const errorMessage = handleApiError(error, 'fetchInvitations');
      setErrors(prev => ({ ...prev, general: errorMessage }));
    }
  }, [dossier._id, fetchWithErrorHandling, handleApiError]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      clearMessages();

      try {
        await Promise.all([fetchCoAuthors(), fetchInvitations()]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [fetchCoAuthors, fetchInvitations, clearMessages]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const handleAddAuthor = async () => {
    if (!email.trim()) {
      setErrors(prev => ({ ...prev, invitation: 'Veuillez saisir une adresse email.' }));
      return;
    }

    if (!validateEmail(email)) {
      setErrors(prev => ({ ...prev, invitation: 'Veuillez saisir une adresse email valide.' }));
      return;
    }

    setSubmitting(true);
    clearMessages();

    try {
      const data = await fetchWithErrorHandling(`/invitations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          dossierId: dossier._id,
          email: email.trim().toLowerCase(),
        }),
      });

      // Mise à jour de l'état local
      if (data.invitation) {
        setInvitations(prev => [...prev, data.invitation]);
      }

      onAddAuthor(email);
      setEmail('');
      setSuccessMessage('Invitation envoyée avec succès !');

      // Effacer le message de succès après 3 secondes
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (error) {
      const errorMessage = handleApiError(error, 'addAuthor');
      setErrors(prev => ({ ...prev, invitation: errorMessage }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveAuthor = async (authorId: string) => {
    if (!authorId) return;

    clearMessages();

    try {
      await fetchWithErrorHandling(`/dossiers/${dossier._id}/authors/${authorId}`, {
        method: 'DELETE',
      });

      // Mise à jour de l'état local après succès
      setCoAuthors(prev => prev.filter(author => author._id !== authorId));
      onRemoveAuthor(authorId);
      setSuccessMessage('Co-auteur supprimé avec succès.');

      // Effacer le message de succès après 3 secondes
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (error) {
      const errorMessage = handleApiError(error, 'removeAuthor');
      setErrors(prev => ({ ...prev, removal: errorMessage }));
    }
  };

  const handleRemoveInvitation = async (invitationId: string) => {
    if (!invitationId) return;

    clearMessages();

    try {
      await fetchWithErrorHandling(`/invitations/${invitationId}`, {
        method: 'DELETE',
      });

      // Mise à jour de l'état local après succès
      setInvitations(prev => prev.filter(inv => inv._id !== invitationId));
      setSuccessMessage('Invitation annulée avec succès.');

      // Effacer le message de succès après 3 secondes
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (error) {
      const errorMessage = handleApiError(error, 'removeInvitation');
      setErrors(prev => ({ ...prev, removal: errorMessage }));
    }
  };

  const getStatusText = (status: string): string => {
    switch (status) {
      case 'pending': return 'En attente';
      case 'accepted': return 'Acceptée';
      case 'rejected': return 'Refusée';
      default: return status;
    }
  };

  const pendingInvitations = invitations.filter(invitation => invitation.status !== 'accepted');

  return (
    <div>
      <p className="text-base font-semibold text-mcdm-third-500 dark:text-mcdm-primary-100 mb-4">
        Gestion des auteurs
      </p>

      {/* Messages d'erreur et de succès */}
      {errors.general && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-lg text-sm">
          {errors.general}
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 rounded-lg text-sm">
          {successMessage}
        </div>
      )}

      {/* Formulaire d'ajout d'auteur */}
      <div className="mb-6">
        <div className="flex gap-2">
          <input
            type="email"
            placeholder="Inviter un co-auteur par email..."
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (errors.invitation) {
                setErrors(prev => ({ ...prev, invitation: undefined }));
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && email.trim() && !submitting) {
                handleAddAuthor();
              }
            }}
            disabled={submitting}
            className={`flex-1 py-2 px-3 rounded-lg border bg-white dark:bg-mcdm-third-500 text-sm text-mcdm-third-500 dark:text-mcdm-primary-100 placeholder-mcdm-third-300 dark:placeholder-mcdm-primary-300 focus:ring-2 focus:ring-mcdm-secondary-400 focus:border-transparent outline-none ${
              errors.invitation
                ? 'border-red-400 dark:border-red-600'
                : 'border-mcdm-primary-200 dark:border-mcdm-third-300'
            }`}
          />
          <button
            onClick={handleAddAuthor}
            disabled={submitting || !email.trim()}
            className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors ${
              submitting || !email.trim()
                ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed'
                : 'bg-mcdm-green-400 hover:bg-mcdm-green-500'
            }`}
          >
            {submitting ? 'Envoi...' : 'Inviter'}
          </button>
        </div>
        {errors.invitation && (
          <p className="text-xs text-red-600 dark:text-red-400 mt-2">{errors.invitation}</p>
        )}
      </div>

      {/* Liste des co-auteurs */}
      <div className="mb-6">
        <h4 className="text-sm uppercase tracking-wider text-mcdm-third-300 dark:text-mcdm-primary-200 font-semibold mb-4 border-b border-mcdm-primary-200 dark:border-mcdm-third-300 pb-2">
          Co-auteurs actuels
        </h4>
        {errors.removal && (
          <div className="mb-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-lg text-sm">
            {errors.removal}
          </div>
        )}

        {loading ? (
          // Skeleton loading
          <div className="space-y-2">
            {[...Array(Math.max(1, dossier.coAuthors?.length || 0))].map((_, index) => (
              <div key={index} className="flex justify-between items-center p-3 bg-mcdm-primary-200 dark:bg-mcdm-third-300 rounded-lg animate-pulse">
                <div className="h-4 bg-mcdm-primary-300 dark:bg-mcdm-third-200 rounded w-2/3"></div>
                <div className="h-5 w-5 bg-mcdm-primary-300 dark:bg-mcdm-third-200 rounded"></div>
              </div>
            ))}
          </div>
        ) : coAuthors.length > 0 ? (
          <div className="overflow-hidden rounded-lg border border-mcdm-primary-200 dark:border-mcdm-third-300">
            <div className="divide-y divide-mcdm-primary-200 dark:divide-mcdm-third-300">
              {coAuthors.map((author) => (
                <div
                  key={author._id}
                  className="flex justify-between items-center px-4 py-3 hover:bg-mcdm-primary-200/30 dark:hover:bg-mcdm-third-300/30 transition-colors"
                >
                  <div>
                    <p className="font-medium text-mcdm-third-500 dark:text-mcdm-primary-100 text-sm">
                      {author.firstName || 'Prénom inconnu'} {author.lastName || 'Nom inconnu'}
                    </p>
                    <p className="text-xs text-mcdm-third-300 dark:text-mcdm-primary-300">
                      {author.email || 'Email inconnu'}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemoveAuthor(author._id)}
                    className="p-1.5 rounded-md text-red-500 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors"
                    title="Supprimer ce co-auteur"
                  >
                    <Trash fill="currentColor" width="16" height="16" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-sm text-mcdm-third-300 dark:text-mcdm-primary-300 italic py-2">
            Aucun co-auteur pour le moment.
          </p>
        )}
      </div>

      {/* Liste des invitations */}
      <div>
        <h4 className="text-sm uppercase tracking-wider text-mcdm-third-300 dark:text-mcdm-primary-200 font-semibold mb-4 border-b border-mcdm-primary-200 dark:border-mcdm-third-300 pb-2">
          Invitations envoyées
        </h4>
        {loading ? (
          // Skeleton loading pour les invitations
          <div className="space-y-2">
            {[...Array(2)].map((_, index) => (
              <div key={index} className="flex justify-between items-center p-3 bg-mcdm-primary-200 dark:bg-mcdm-third-300 rounded-lg animate-pulse">
                <div className="h-4 bg-mcdm-primary-300 dark:bg-mcdm-third-200 rounded w-1/2"></div>
                <div className="h-5 w-5 bg-mcdm-primary-300 dark:bg-mcdm-third-200 rounded"></div>
              </div>
            ))}
          </div>
        ) : pendingInvitations.length > 0 ? (
          <div className="overflow-hidden rounded-lg border border-mcdm-primary-200 dark:border-mcdm-third-300">
            <div className="divide-y divide-mcdm-primary-200 dark:divide-mcdm-third-300">
              {pendingInvitations.map((invitation) => (
                <div
                  key={invitation._id}
                  className="flex justify-between items-center px-4 py-3 hover:bg-mcdm-primary-200/30 dark:hover:bg-mcdm-third-300/30 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-mcdm-third-500 dark:text-mcdm-primary-100">
                      {invitation.email}
                    </span>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                      invitation.status === 'pending'
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                    }`}>
                      {getStatusText(invitation.status)}
                    </span>
                  </div>
                  <button
                    onClick={() => handleRemoveInvitation(invitation._id)}
                    className="p-1.5 rounded-md text-red-500 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors"
                    title="Annuler cette invitation"
                  >
                    <Trash fill="currentColor" width="16" height="16" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-sm text-mcdm-third-300 dark:text-mcdm-primary-300 italic py-2">
            Aucune invitation en attente.
          </p>
        )}
      </div>
    </div>
  );
};

export default AuthorsSettings;